// Turns a dashboard gadget's live, already-resolved scope into the fragment the
// Insights agent reads. Pure by design so the resolution logic (which filters the
// agent can reproduce, how a comparison reads) is unit-testable without mounting a
// gadget — useRegisterDashboardGadgetScope is the thin hook that gathers the
// contexts and calls this.
//
// A dashboard gadget's scope is ABSOLUTE: unlike a report gadget, whose fields are
// deltas from a shared report scope, there is no view-level scope to diff against,
// so every date and filter is reported outright.

import {
  BaseViewsLookup,
  MAX_VIEW_FILTERS,
  toViewFilter,
} from 'components/InsightsChat/insightsViewGadget';
import describeComparison from 'formatters/describeComparison';
import {
  InsightsGadgetScopeFragment,
  viewFilterClauses,
} from 'contextProviders/InsightsContentProvider/viewTypes';

const buildGadgetScopeFragment = ({
  dateScope,
  scopeAsFixed,
  drillDownsAsFixed,
  baseViews,
  comparison,
  weekStartsOn,
}: {
  // Already resolved to concrete dates by useDateScope.
  dateScope: DateRangeInput;
  scopeAsFixed: FixedFilter[];
  drillDownsAsFixed: FixedFilter[];
  baseViews: BaseViewsLookup;
  comparison?: PersistedComparisonType;
  weekStartsOn?: WeekStartsOn;
}): InsightsGadgetScopeFragment => {
  const allFilters = [...scopeAsFixed, ...drillDownsAsFixed].map((filter) =>
    toViewFilter({ filter, baseViews }),
  );
  const filters = allFilters.slice(0, MAX_VIEW_FILTERS);

  // A filter with no clauses (wildcard, date drill-down, "blank OR value")
  // can't be reproduced by a metric-query tool — the server prose warns the
  // user their recomputed figure may legitimately differ.
  const hasUnreproducibleFilters = allFilters.some(
    (f) => viewFilterClauses(f).length === 0,
  );

  const comparisonText = describeComparison(comparison);

  const ownScope = {
    ...(dateScope.startDate ? { startDate: dateScope.startDate } : {}),
    ...(dateScope.endDate ? { endDate: dateScope.endDate } : {}),
    ...(dateScope.dateField ? { dateField: dateScope.dateField } : {}),
    ...(weekStartsOn ? { weekStartsOn } : {}),
    ...(comparisonText ? { comparison: comparisonText } : {}),
    ...(filters.length ? { filters } : {}),
  };

  return { ownScope, hasUnreproducibleFilters };
};

export default buildGadgetScopeFragment;
